// Resolve a site's verified "from" email address from its GENERAL_VERIFIED_EMAILS
// setting. THE single frontend implementation — import this rather than adding
// another copy.
//
// WHY THIS IS NON-TRIVIAL: the setting has FIVE possible shapes, because two
// writers disagree and neither normalizes.
//
//   1. JSON array of strings   ["office@x.com"]
//   2. JSON object             {"email":"office@x.com"}
//   3. JSON string             "office@x.com"
//   4. BARE string, not JSON   office@x.com        ← 100% of prod rows today
//   5. CSV list                a@x.com,b@x.com     ← what teamio-admin writes
//
//   - main-service/email-settings.js (the SendGrid setup UI) writes shape 4 and
//     OVERWRITES the value.
//   - teamio-admin/admin.js addVerifiedEmail does emails.join(',') — writes
//     shape 5 and APPENDS.
//
// A 2026-07-29 prod audit found all 8 live rows are shape 4, purely because nobody
// has added a second sender through the admin tool yet. Readers that pass
// PropertyValue through untouched work only because of that; the first CSV value
// would hand SendGrid "a@x.com,b@x.com" as a From address and be rejected.
// Readers that JSON.parse-only have the opposite bug and resolve to nothing for
// every real site, silently falling back to noreply@teamio.app.
//
// Backend counterpart: tx-presenter-service/lib/resolveChannels.js
// (same contract; that file additionally resolves SMS/email channel availability).

/**
 * @param {string|object|null|undefined} raw
 *   Either the raw PropertyValue string, or the whole settings row
 *   (`{ PropertyValue }`) — components usually hold the row via `allSettings`,
 *   so both are accepted to keep call sites clean.
 * @returns {string|null} the first usable address, or null if there isn't one
 */
export function pickVerifiedFromEmail(raw) {
  // Settings-row convenience: allSettings.GENERAL_VERIFIED_EMAILS
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && raw.PropertyValue !== undefined) {
    return pickVerifiedFromEmail(raw.PropertyValue);
  }
  if (!raw || typeof raw !== 'string') return null;

  // A candidate may still be a CSV/semicolon list even after JSON unwrapping —
  // "a@x.com,b@x.com" is both a valid JSON string and a list. Take the FIRST
  // address: callers need exactly one From, and the admin UI keeps the original
  // sender first.
  const firstAddress = (v) => {
    if (!v || typeof v !== 'string') return null;
    const hit = v.split(/[,;]/).map((s) => s.trim()).find((s) => s.includes('@'));
    return hit || null;
  };

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return firstAddress(raw);                                   // shapes 4 + 5
  }
  if (typeof parsed === 'string') return firstAddress(parsed);  // shape 3
  if (Array.isArray(parsed)) {                                  // shape 1
    for (const entry of parsed) {
      const v = firstAddress(typeof entry === 'string' ? entry : (entry && entry.email));
      if (v) return v;
    }
    return null;
  }
  return firstAddress(parsed && parsed.email);                  // shape 2
}

/**
 * Same resolution, with a caller-supplied fallback for send paths that must always
 * produce something. Defaults to the shared-account sender that every existing
 * fallback in the codebase already uses.
 *
 * Use this for "build a From header"; use pickVerifiedFromEmail when you need to
 * know whether the site HAS a verified sender (i.e. as an entitlement check),
 * since the fallback would mask that.
 */
export function resolveFromEmail(raw, fallback = 'noreply@teamio.app') {
  return pickVerifiedFromEmail(raw) || fallback;
}

/**
 * Full list, for UIs that let a user pick among a site's verified senders
 * (Workflow builders, wiki email previews). Same shape tolerance as above.
 *
 * @returns {string[]} every address found, in stored order (may be empty)
 */
export function listVerifiedEmails(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && raw.PropertyValue !== undefined) {
    return listVerifiedEmails(raw.PropertyValue);
  }
  if (!raw || typeof raw !== 'string') return [];

  const split = (v) => (typeof v === 'string' ? v.split(/[,;]/).map((s) => s.trim()).filter((s) => s.includes('@')) : []);

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return split(raw);
  }
  if (typeof parsed === 'string') return split(parsed);
  if (Array.isArray(parsed)) {
    return parsed.flatMap((entry) => split(typeof entry === 'string' ? entry : (entry && entry.email)));
  }
  return split(parsed && parsed.email);
}
